// เก็บ/คืนสถานะทั้งหมดของตัวแก้ PDF (stamp_layout v2 ของสัญญา) และแปลง layout แบบเก่าของ PdfStampEditor
import { normRot, rotateVec, visibleSize, visibleToNative } from "./geometry";

const round3 = (v) => Math.round(v * 1000) / 1000;
const roundDeep = (value) => {
  if (typeof value === "number") return round3(value);
  if (Array.isArray(value)) return value.map(roundDeep);
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, roundDeep(v)]));
  return value;
};

// hash สั้นๆ สำหรับ dedupe รูปที่ซ้ำกัน (FNV-1a สองรอบ + ความยาว กันชนกันโดยบังเอิญ)
export const hashString = (str) => {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < str.length; i += 1) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = Math.imul(h2 + c, 2246822519) >>> 0;
  }
  return `${h1.toString(16)}${h2.toString(16)}${str.length.toString(16)}`;
};

// pages = state ของตัวแก้ ([{ srcIndex, userRotation, objects }]) — ไม่เก็บ id/ขนาดหน้า (อ่านจากต้นฉบับตอนเปิด)
// หน้าที่ถูกลบ = srcIndex ที่ไม่อยู่ในรายการ, ลำดับหน้า = ลำดับในอาร์เรย์
export function buildLayoutV2(pages) {
  const images = {};
  const outPages = pages.map((page) => ({
    srcIndex: page.srcIndex,
    userRotation: page.userRotation || 0,
    objects: page.objects.map((obj) => {
      const { id, src, ...rest } = obj;
      if (obj.type === "image") {
        const key = hashString(src);
        images[key] = src;
        return roundDeep({ ...rest, img: key });
      }
      return roundDeep(rest);
    }),
  }));
  return { version: 2, pages: outPages, images };
}

// docPages = หน้าที่อ่านจากต้นฉบับ (มี W,H,baseRotation) -> หน้าตาม layout (ลำดับ/หมุน/ออบเจกต์เดิม)
// ข้อมูลเสีย/ไม่ครบ = throw (ห้ามเปิดตัวแก้เปล่า เพราะบันทึกแล้วของเดิมจะหายถาวร)
export function restoreLayoutV2(layout, docPages, nextId) {
  if (!Array.isArray(layout.pages) || !layout.pages.length) throw new Error("ข้อมูลการจัดวางเดิมเสียหาย");
  const images = layout.images || {};
  const out = [];
  layout.pages.forEach((lp) => {
    const base = docPages[lp.srcIndex];
    if (!base) throw new Error("ข้อมูลการจัดวางเดิมไม่ตรงกับไฟล์ต้นฉบับ");
    out.push({
      ...base,
      id: nextId("p"),
      userRotation: normRot(lp.userRotation || 0),
      objects: (lp.objects || [])
        .map((o) => {
          const { img, ...rest } = o;
          return { ...rest, id: nextId("o"), ...(img ? { src: images[img] } : null) };
        })
        .map((o) => {
          if (o.type === "image" && !o.src) throw new Error("ข้อมูลรูปในการจัดวางเดิมสูญหาย");
          return o;
        }),
    });
  });
  return out;
}

// layout เก่า (PdfStampEditor): [{ page(1-based), stamp_uid, x,y,w,h (สัดส่วนของหน้าที่เห็น), rotate (ตามเข็ม), opacity }]
// -> ออบเจกต์รูป ให้ตำแหน่ง/ขนาด/การหมุนตรงกับที่เคยประทับ
export function legacyLayoutToPages(layout, docPages, stampImages, nextId) {
  const byIndex = {};
  layout.forEach((p) => {
    const page = docPages[(p.page || 0) - 1];
    const src = stampImages?.[p.stamp_uid];
    if (!page || !src) throw new Error("แปลงตราเดิมไม่ได้ (ไม่พบหน้าหรือภาพตราเดิม)");
    const R = page.baseRotation || 0;
    const v = visibleSize(page.W, page.H, R);
    const w = p.w * v.w;
    const h = p.h * v.h;
    const rot = normRot((p.rotate || 0) - R); // มุมที่เห็น = rot + R
    const c = visibleToNative((p.x + p.w / 2) * v.w, (p.y + p.h / 2) * v.h, page.W, page.H, R);
    const off = rotateVec(w / 2, h / 2, rot);
    (byIndex[page.srcIndex] = byIndex[page.srcIndex] || []).push({
      id: nextId("o"), type: "image", x: c.x - off.x, y: c.y - off.y, rot, w, h, src, opacity: p.opacity ?? 1,
    });
  });
  return docPages.map((pg) => ({ ...pg, objects: byIndex[pg.srcIndex] || [] }));
}
